import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '@/components/Layout'
import { HeritageBadge, PrimaryButton, PolaroidNote } from '@/components/atoms'
import { BreadcrumbJsonLd } from '@/components/shared/json-ld'

const whatWeTake: string[] = [
  "Hybrids, road, gravel, mountain — every category",
  "Older ebikes — even ones with retired batteries",
  "Cargo bikes",
  "Kid bikes (we resell to Boulder families)",
  "Frames-only, if they're nice",
]

function TradeIn() {
  return (
    <>
      <BreadcrumbJsonLd items={[{ name: "Trade-In", href: "/trade-in" }]} />

      {/* Hero — 2-column grid */}
      <section className="bg-[var(--background-warm)]" style={{ padding: "72px 0 56px" }}>
        <div className="container">
          <div
            className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] items-start"
            style={{ gap: 56 }}
          >
            {/* Left: badge + h1 + sub-copy */}
            <div>
              <HeritageBadge>Trade-in program</HeritageBadge>
              <h1
                className="mt-6 text-[clamp(2.4rem,7vw,4rem)] md:text-[64px]"
                style={{ fontFamily: "var(--font-head)", lineHeight: 1.05 }}
              >
                Bring in your old bike.
                <br />
                <span
                  className="hand text-[clamp(2.5rem,7.4vw,4.375rem)] md:text-[70px]"
                  style={{ color: "var(--accent)" }}
                >
                  walk out with credit.
                </span>
              </h1>
              {/*
                FACT-CHECK: removed "$400-$1,200 in store credit"
                trade-in range — unverified pricing claim. Replaced
                with deflection to a per-bike quote, which is the
                actual workflow anyway. Tracking:
                docs/migration/RYAN-FACT-CHECK-ASK.md
              */}
              <p className="mt-6 text-lg" style={{ color: "var(--muted-foreground)", maxWidth: 560 }}>
                {"We buy bikes back — yours, your neighbor's, the one in the garage that hasn't moved in a while. Send a photo and we'll quote you store credit toward your next bike."}
              </p>
            </div>

            {/* Right: quote card */}
            <div
              className="bg-card border border-border rounded-[var(--radius-lg)] shadow-[var(--shadow-md)]"
              style={{ padding: 28 }}
            >
              <p className="eyebrow text-accent">Get a no-commitment quote</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Send us a photo and a quick description. We reply within 24 hours — usually faster.
              </p>
              <div className="mt-6">
                <Link href="/contact">
                  <PrimaryButton hand="Send for a quote" sub="Reply within 24 hours · usually faster" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we take section */}
      <section className="bg-[var(--background)] py-[72px]">
        <div className="container">
          <div
            className="grid grid-cols-1 md:grid-cols-[1fr_1fr] items-start"
            style={{ gap: 56 }}
          >
            {/* Left: checklist */}
            <div>
              <p className="eyebrow text-muted-foreground">What we take</p>
              <h2
                className="hand mt-2"
                style={{ fontSize: 36, color: "var(--foreground)" }}
              >
                (almost anything ridable)
              </h2>
              <p className="mt-3 text-lg font-semibold text-foreground">
                If it has wheels,
              </p>
              <ul className="mt-4 space-y-3">
                {whatWeTake.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="hand text-[22px] text-accent leading-none mt-0.5">✓</span>
                    <span className="text-base text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: PolaroidNote — signature moment */}
            <div className="flex items-center justify-center pt-8 md:pt-0">
              <PolaroidNote rotate={2}>
                We refurbish what we take in and
                resell it at fair prices to riders who
                need a leg up. Your old bike becomes{" "}
                <em>{"someone's first ride."}</em> Good karma all
                around.
              </PolaroidNote>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

TradeIn.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default TradeIn
